import useUpdateSubitemTableLayout from "api/api-hooks/subitems/useUpdateSubitemTableLayout";
import useUpdateSubitemField from "api/api-hooks/subitems/useUpdateSubitemField";
import TableLayoutsButton from "components/TableLayouts/TableLayoutsButton";
import useCloudPermissions from "hooks/useCloudPermissions";
import StrUtils from "utils/StrUtils";
import _ from "underscore";
import { useState } from "react";

const SubitemTableLayout = ({
  module,
  fieldSchema,
  query,
  setQuery,
  parentRecord,
  foundLayout,
  selectedLayout,
  setSelectedLayout,
}) => {
  const { cPermissions } = useCloudPermissions();
  const { updateSubitemTableLayout, updatingSubitemTableLayout } =
    useUpdateSubitemTableLayout();
  const { updateSubitemField } = useUpdateSubitemField();

  const [currentLayouts, setLayouts] = useState(
    fieldSchema?.TableLayouts?.length > 0 ? fieldSchema?.TableLayouts : []
  );
  const [defaultLayout, setDefault] = useState(
    fieldSchema?.Query?.[0]?.Query?._SelectedLayout
  );

  return (
    <TableLayoutsButton
      savedLayout={foundLayout?.Fields}
      savedLayoutID={defaultLayout}
      selectedLayoutName={foundLayout?.Name ?? "Default Layout"}
      selectedLayout={query?.[0]?.Query?.Fields}
      selectedLayoutID={selectedLayout}
      layouts={currentLayouts}
      onSaveAsDefault={(id) => {
        const foundLayout = _.findWhere(fieldSchema?.TableLayouts, {
          id,
        });

        const newQuery = [
          {
            QueryField: query[0].QueryField,
            Query: {
              ...query[0].Query,
              Fields: foundLayout.Fields,
              _SelectedLayout: id,
            },
          },
        ];

        setSelectedLayout(id);
        setQuery(newQuery);

        updateSubitemField({
          moduleAPIName: parentRecord?._ModuleAPIName,
          fieldAPIName: fieldSchema?.APIName,
          payload: {
            Query: newQuery,
          },
        });
        setDefault(id);
      }}
      onSelect={(id, { Fields }) => {
        setSelectedLayout(id);
        setQuery(
          query.map((item, i) => {
            if (i === 0) {
              return { ...item, Query: { ...item.Query, Fields } };
            }
            return item;
          })
        );
      }}
      updateLayout={updateItem}
      createLayout={createItem}
      submitting={updatingSubitemTableLayout}
      deleteLayout={deleteItem}
      reorderLayouts={reorderItems}
      currentModule={module}
      validPermissions={cPermissions.cloud_modify_field}
      field={fieldSchema}
      usePageSelection={false}
    />
  );

  function createItem(layout) {
    let newLayout = layout.payload;
    newLayout.id = StrUtils.uuidv4();
    currentLayouts.push(newLayout);

    updateSubitemTableLayout({
      moduleAPIName: parentRecord._ModuleAPIName,
      fieldAPIName: fieldSchema.APIName,
      payload: {
        TableLayouts: currentLayouts,
      },
    });
    layout.onSuccess();
  }

  function updateItem(layout) {
    let updatedLayout = layout.payload;
    updatedLayout.id = layout.id;
    const savedQuery = fieldSchema?.Query?.[0];

    const updatedLayouts = currentLayouts.map((item) =>
      item.id === updatedLayout.id ? updatedLayout : item
    );

    if (selectedLayout === updatedLayout.id) {
      setQuery((prev) =>
        prev.map((q) => ({
          ...q,
          Query: { ...query, Fields: updatedLayout.Fields },
        }))
      );
    }

    if (updatedLayout.id === savedQuery?.Query?._SelectedLayout) {
      updateSubitemField({
        moduleAPIName: parentRecord?._ModuleAPIName,
        fieldAPIName: fieldSchema?.APIName,
        payload: {
          Query: [
            {
              ...(savedQuery ?? {}),
              Query: {
                ...(savedQuery.Query ?? {}),
                Fields: updatedLayout.Fields,
              },
            },
          ],
          TableLayouts: updatedLayouts,
        },
      });

      return layout.onSuccess();
    }

    updateSubitemTableLayout({
      moduleAPIName: parentRecord._ModuleAPIName,
      fieldAPIName: fieldSchema.APIName,
      payload: {
        TableLayouts: updatedLayouts,
      },
      onSuccess: (data) => {
        const newLayouts = data?.updatedField?.TableLayouts;
        newLayouts && setLayouts(newLayouts);
      },
    });

    layout.onSuccess();
  }

  // TODO: Prevent deletion if it is the last layout.
  function deleteItem(layout) {
    const newLayouts = currentLayouts.filter((item) => item.id !== layout.id);
    setLayouts(newLayouts);

    if (layout.id === query[0].Query._SelectedLayout) {
      const updatedQuery = [
        {
          QueryField: query[0].QueryField,
          Query: {
            ...query[0].Query,
            _SelectedLayout: "",
            Fields: fieldSchema.SubitemFields.map((item) => item.APIName),
          },
        },
      ];

      setQuery(updatedQuery);

      return updateSubitemField({
        moduleAPIName: parentRecord?._ModuleAPIName,
        fieldAPIName: fieldSchema?.APIName,
        payload: {
          Query: updatedQuery,
          TableLayouts: currentLayouts,
        },
      });
    }

    updateSubitemTableLayout({
      moduleAPIName: parentRecord._ModuleAPIName,
      fieldAPIName: fieldSchema.APIName,
      payload: {
        TableLayouts: newLayouts,
      },
    });
  }

  function reorderItems(reorderItems) {
    let newOrderedIds = reorderItems.newOrder;
    const newLayouts = [...currentLayouts];

    newLayouts.sort((a, b) => {
      const aIndex = newOrderedIds.indexOf(a.id);
      const bIndex = newOrderedIds.indexOf(b.id);
      return aIndex - bIndex;
    });

    setLayouts(newLayouts);
    updateSubitemTableLayout({
      moduleAPIName: parentRecord._ModuleAPIName,
      fieldAPIName: fieldSchema.APIName,
      payload: {
        TableLayouts: newLayouts,
      },
    });
  }
};

export default SubitemTableLayout;
